import { useState } from "react";
import { Pencil, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "../../lib/adminApi";
import AdminModal, { Field, inputCls, ImageField } from "./AdminModal";

const EMPTY = { title: "", excerpt: "", image: "", url: "", content: "", order: 99 };

const AdminBlog = ({ posts, reload }) => {
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);

    const openNew = () => setEditing({ ...EMPTY });
    const openEdit = async (p) => {
        try {
            const { data } = await adminApi.get(`/blog/${p.slug}`);
            setEditing({ slug: data.slug, title: data.title, excerpt: data.excerpt || "", image: data.image || "", url: data.url || "", content: data.content || "", order: data.order ?? 99 });
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        const payload = { title: editing.title, excerpt: editing.excerpt, image: editing.image, url: editing.url, content: editing.content, order: Number(editing.order) || 99 };
        try {
            if (editing.slug) {
                await adminApi.put(`/admin/blog/${editing.slug}`, payload);
                toast.success("Artículo actualizado");
            } else {
                await adminApi.post("/admin/blog", payload);
                toast.success("Artículo creado");
            }
            setEditing(null);
            reload();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (p) => {
        if (!window.confirm(`¿Eliminar "${p.title}"? Esta acción no se puede deshacer.`)) return;
        try {
            await adminApi.delete(`/admin/blog/${p.slug}`);
            toast.success("Artículo eliminado");
            reload();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div data-testid="admin-blog-panel">
            <div className="flex items-center justify-between">
                <p className="text-sm text-brand-muted">{posts.length} artículos</p>
                <button
                    data-testid="blog-new-button"
                    onClick={openNew}
                    className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-brand-magenta px-6 font-display text-sm font-semibold text-white transition-colors hover:bg-brand-magentadark"
                >
                    <Plus className="h-4 w-4" /> Nuevo artículo
                </button>
            </div>
            <ul className="mt-6 space-y-3">
                {posts.map((p) => (
                    <li
                        key={p.slug}
                        data-testid={`blog-row-${p.slug}`}
                        className="flex items-center gap-4 rounded-2xl border border-black/10 bg-white p-3 shadow-soft"
                    >
                        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-black/[0.03]">
                            {p.image && <img src={p.image} alt={p.title} className="h-full w-full object-cover" loading="lazy" />}
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="truncate font-display font-semibold text-brand-ink">{p.title}</p>
                            <p className="truncate text-xs text-brand-muted">{p.url || "Sin enlace"}</p>
                        </div>
                        <button
                            data-testid={`blog-edit-${p.slug}`}
                            onClick={() => openEdit(p)}
                            aria-label={`Editar ${p.title}`}
                            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-brand-ink transition-colors hover:bg-black/5"
                        >
                            <Pencil className="h-4 w-4" />
                        </button>
                        <button
                            data-testid={`blog-delete-${p.slug}`}
                            onClick={() => remove(p)}
                            aria-label={`Eliminar ${p.title}`}
                            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-brand-magenta transition-colors hover:bg-brand-magenta/10"
                        >
                            <Trash2 className="h-4 w-4" />
                        </button>
                    </li>
                ))}
            </ul>

            {editing && (
                <AdminModal
                    testId="blog-form"
                    title={editing.slug ? "Editar artículo" : "Nuevo artículo"}
                    onClose={() => setEditing(null)}
                    onSubmit={save}
                    saving={saving}
                >
                    <Field label="Título *">
                        <input data-testid="blog-form-title" required value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className={inputCls} />
                    </Field>
                    <Field label="Resumen">
                        <textarea data-testid="blog-form-excerpt" rows={3} value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} className={`${inputCls} py-3`} />
                    </Field>
                    <ImageField testId="blog-form-image" value={editing.image} onChange={(url) => setEditing({ ...editing, image: url })} />
                    <Field label="Enlace original (opcional)">
                        <input data-testid="blog-form-url" type="url" value={editing.url} onChange={(e) => setEditing({ ...editing, url: e.target.value })} className={inputCls} placeholder="https://..." />
                    </Field>
                    <Field label="Contenido del artículo (usa ## para títulos y - para listas)">
                        <textarea data-testid="blog-form-content" rows={10} value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} className={`${inputCls} py-3`} placeholder="Escribe el artículo completo aquí..." />
                    </Field>
                    <Field label="Orden (menor aparece primero)">
                        <input data-testid="blog-form-order" type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} className={inputCls} />
                    </Field>
                </AdminModal>
            )}
        </div>
    );
};

export default AdminBlog;
